import React from 'react';
import SEOHead from '../../components/seo/seo-head';
import { SEO_CONFIG } from '../../constants/seo-config';
import { browserHistory } from 'react-router';
import queryString from 'query-string';
import LoginStore from '../../stores/login-store';
import AppRoutes from '../../routes/routes';
import MarketingSubscribe from '../../components/homepage/marketing-subscribe';
import PostHogService from '../../services/posthog';
import PostHogEvents from '../../constants/posthog-events';

const registerQuery = queryString.stringify({ modal: 'register' });

const TRIAL_DAYS = 7; // matches User#free_trial_expires_at in the API

const FREE_FEATURES = [
  'Full access to every checklist',
  'Axon AI — clinical question answering',
  'Download checklists for presentations',
  'Keep personal notes and bookmarks',
];

const PREMIUM_FEATURES = [
  'Unlimited access to 4,000+ checklists',
  'Bookmark all checklists for easy access',
  'Axon AI — clinical question answering',
  'Inline citations linked to PubMed & guidelines',
  'Keep personal notes and reminders',
  'Easy download for presentations',
  'View popular and recently updated checklists',
  'Student discount with institutional email',
];

export default class MarketingPricing extends React.Component {
  constructor(props) {
    super(props);
    this.registerModal = this.registerModal.bind(this);
    this.upgradeModal = this.upgradeModal.bind(this);
    this.downgradeModal = this.downgradeModal.bind(this);
  }

  componentDidMount() {
    this.initReveal();
  }

  componentDidUpdate() {
    this.initReveal();
  }

  initReveal() {
    const reveals = document.querySelectorAll('.reveal:not(.visible)');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.12 });
      reveals.forEach(el => io.observe(el));
    } else {
      reveals.forEach(el => el.classList.add('visible'));
    }
  }

  registerModal() {
    PostHogService.capture(PostHogEvents.PRICING_PLAN_CLICKED, { plan: 'free_trial' });
    browserHistory.replace(`${this.props.location.pathname}?${registerQuery}`);
  }

  upgradeModal(e) {
    e && e.preventDefault();
    const query = queryString.stringify({ modal: 'upgrade' });
    browserHistory.replace(`${this.props.location.pathname}?${query}`);
  }

  downgradeModal(e) {
    e && e.preventDefault();
    const query = queryString.stringify({ modal: 'cancel-subscription' });
    browserHistory.replace(`${this.props.location.pathname}?${query}`);
  }

  render() {
    const isLoggedIn = LoginStore.isLoggedIn();
    let account_type = 'Starter';
    let trialExpired = false;
    if (isLoggedIn) {
      const userData = LoginStore.getSessionUserData();
      if (userData) {
        account_type = userData.account_type;
        trialExpired = Boolean(userData.free_trial_expired);
      }
    }

    return (
      <div className="new-homepage-wrapper">
        <SEOHead {...SEO_CONFIG.pricing} />

        {/* Header */}
        <div className="section" style={{ paddingTop: 112, textAlign: 'center' }}>
          <div className="eyebrow reveal" style={{ justifyContent: 'center' }}>
            <div className="eyebrow-line"></div>
            <span className="eyebrow-text">Pricing</span>
            <div className="eyebrow-line"></div>
          </div>
          <h1 className="section-title reveal reveal-delay-1">
            Simple, transparent<br /><em>clinical access</em>
          </h1>
          <p className="section-sub reveal reveal-delay-2">
            Try everything free for {TRIAL_DAYS} days, then choose a plan to keep full access. No hidden fees, no commitment.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="section" style={{ paddingTop: 0 }}>
          <div className="pricing-grid reveal reveal-delay-1">

            {/* Free tier */}
            <div className="price-card">
              <div className="price-tier">Free Trial</div>
              <div className="price-amount">£0</div>
              <div className="price-period">for {TRIAL_DAYS} days</div>
              <div className="price-desc">Full access to the whole database while you try it out. No credit card required.</div>
              <div className="price-divider"></div>
              <ul className="price-features">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i}><span className="pf-icon">✓</span>{f}</li>
                ))}
              </ul>

              {isLoggedIn ? (
                account_type === 'Starter' ? (
                  <button className="price-btn-outline" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                    {trialExpired ? 'Trial ended' : 'Current plan'}
                  </button>
                ) : null
              ) : (
                <button className="price-btn-outline" onClick={this.registerModal}>
                  Get started free
                </button>
              )}
            </div>

            {/* Premium monthly */}
            <div className="price-card featured">
              <div className="price-badge">Most Popular</div>
              <div className="price-tier">Premium Monthly</div>
              <div className="price-amount">£4.99</div>
              <div className="price-period">per month</div>
              <div className="price-desc">Full unlimited access for clinicians, researchers and students.</div>
              <div className="price-divider"></div>
              <ul className="price-features">
                {PREMIUM_FEATURES.map((f, i) => (
                  <li key={i}><span className="pf-icon">✓</span>{f}</li>
                ))}
              </ul>
              {isLoggedIn ? (
                account_type === 'Starter' ? (
                  <button className="price-btn-primary" onClick={this.upgradeModal}>
                    Upgrade now
                  </button>
                ) : (
                  <button className="price-btn-outline" onClick={this.downgradeModal}>
                    Cancel subscription
                  </button>
                )
              ) : (
                <button className="price-btn-primary" onClick={this.registerModal}>
                  Start free trial →
                </button>
              )}
            </div>

            {/* Premium annual */}
            <div className="price-card">
              <div className="price-tier">Premium Annual</div>
              <div className="price-amount">£49.99</div>
              <div className="price-period">per year — 2 months free</div>
              <div className="price-desc">12 months of unlimited access for the price of 10. Best value for committed users.</div>
              <div className="price-divider"></div>
              <ul className="price-features">
                {PREMIUM_FEATURES.map((f, i) => (
                  <li key={i}><span className="pf-icon">✓</span>{f}</li>
                ))}
              </ul>
              {isLoggedIn ? (
                account_type === 'Starter' ? (
                  <button className="price-btn-outline" onClick={this.upgradeModal}>
                    Upgrade now
                  </button>
                ) : (
                  <button className="price-btn-outline" onClick={this.downgradeModal}>
                    Cancel subscription
                  </button>
                )
              ) : (
                <button className="price-btn-outline" onClick={this.registerModal}>
                  Start free trial →
                </button>
              )}
            </div>

          </div>

          {isLoggedIn ? null : (
            <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
              Create your account in minutes.{' '}
              <a href={AppRoutes.tcs} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-600)' }}>
                Terms &amp; conditions
              </a>{' '}
              apply. Student discount available with a valid institutional email address.
            </p>
          )}
        </div>

        {/* FAQ teaser */}
        <div className="section" style={{ textAlign: 'center' }}>
          <div className="eyebrow reveal" style={{ justifyContent: 'center' }}>
            <div className="eyebrow-line"></div>
            <span className="eyebrow-text">Questions?</span>
            <div className="eyebrow-line"></div>
          </div>
          <h2 className="section-title reveal reveal-delay-1">Everything you need to<br /><em>make the decision</em></h2>
          <div className="feature-grid reveal reveal-delay-2" style={{ maxWidth: 900, margin: '0 auto' }}>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <div className="feature-title">Cancel anytime</div>
              <div className="feature-desc">No lock-in. Cancel your subscription at any time from your account settings. No questions asked.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎓</div>
              <div className="feature-title">Student discount</div>
              <div className="feature-desc">Subscribe with a valid institutional or university email address to automatically qualify for our student rate.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <div className="feature-title">Secure payments</div>
              <div className="feature-desc">Payments are processed securely. We never store your card details on our servers.</div>
            </div>
          </div>
        </div>

        <MarketingSubscribe />
      </div>
    );
  }
}
